import { ArrowLeft, Code2, Link as LinkIcon, Share2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { LoaderFunctionArgs, MetaFunction } from 'react-router';
import {
  Link,
  useLoaderData,
  useNavigate,
  useSearchParams,
} from 'react-router';
import FeatureTechCard from '~/components/tech-spec/FeatureTechCard';
import StackSelector from '~/components/tech-spec/StackSelector';
import TechExportModal from '~/components/tech-spec/TechExportModal';
import { getSession } from '~/lib/auth/session';
import type { BuilderConfig } from '~/lib/builder-types';
import { getSpecBySlug } from '~/lib/db/queries/specs';
import type { Feature } from '~/lib/db/schema';
import type { FeatureDefinition } from '~/lib/feature-modules/types';
import { stackPresets } from '~/lib/tech-spec/stack-presets';
import type {
  FeatureTechSpecDelta,
  TechSpecCustomizations,
  TechSpecState,
  TechStackConfig,
} from '~/lib/tech-spec/types';

export const meta: MetaFunction<typeof loader> = ({ loaderData }) => [
  {
    title: loaderData
      ? `Tech Spec: ${loaderData.spec.name} — Specly`
      : 'Technical Spec — Specly',
  },
];

export async function loader({ params, request }: LoaderFunctionArgs) {
  const spec = await getSpecBySlug(params.slug!);
  if (!spec) throw new Response('Not Found', { status: 404 });
  const session = await getSession(request);
  return { spec, userId: session?.user.id ?? null };
}

type SpecFeatureWithDef = {
  feature: Feature & { definition: FeatureDefinition };
  isRequired: boolean;
};

type MobileTab = 'stack' | 'spec';

const DEFAULT_STACK: TechStackConfig = stackPresets[0].stack; // T3

export default function TechSpec() {
  const { spec } = useLoaderData<typeof loader>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const specFeatures = spec.specFeatures as unknown as SpecFeatureWithDef[];

  // Parse builder config from URL (carried from build page)
  const builderConfig = useMemo<BuilderConfig | null>(() => {
    const raw = searchParams.get('config');
    if (!raw) return null;
    try {
      return JSON.parse(atob(raw)) as BuilderConfig;
    } catch {
      return null;
    }
  }, [searchParams]);

  // Features to show tech spec for
  const enabledFeatures = useMemo(() => {
    if (!builderConfig) return specFeatures;
    return specFeatures.filter(
      (sf) => builderConfig.features[sf.feature.slug]?.enabled,
    );
  }, [builderConfig, specFeatures]);

  // Parse or init tech state from URL
  const getInitialTechState = (): TechSpecState => {
    const raw = searchParams.get('tech');
    if (raw) {
      try {
        return JSON.parse(atob(raw)) as TechSpecState;
      } catch {
        // fall through
      }
    }
    return { stack: DEFAULT_STACK, customizations: {} };
  };

  const [techState, setTechState] =
    useState<TechSpecState>(getInitialTechState);
  const [showExport, setShowExport] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [mobileTab, setMobileTab] = useState<MobileTab>('spec');

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync state → URL
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const params: Record<string, string> = {
        tech: btoa(JSON.stringify(techState)),
      };
      const rawConfig = searchParams.get('config');
      if (rawConfig) params.config = rawConfig;
      navigate(`?${new URLSearchParams(params)}`, {
        replace: true,
        preventScrollReset: true,
      });
    }, 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [techState, navigate, searchParams]);

  const handleStackChange = useCallback((stack: TechStackConfig) => {
    setTechState((prev) => ({ ...prev, stack }));
  }, []);

  const handleCustomize = useCallback(
    (featureSlug: string, delta: Partial<FeatureTechSpecDelta>) => {
      setTechState((prev) => {
        const existing = prev.customizations[featureSlug] ?? {};
        const merged: FeatureTechSpecDelta = {
          pages: {
            added: [
              ...(existing.pages?.added ?? []),
              ...(delta.pages?.added ?? []),
            ],
            removedIds: [
              ...(existing.pages?.removedIds ?? []),
              ...(delta.pages?.removedIds ?? []),
            ],
          },
          endpoints: {
            added: [
              ...(existing.endpoints?.added ?? []),
              ...(delta.endpoints?.added ?? []),
            ],
            removedIds: [
              ...(existing.endpoints?.removedIds ?? []),
              ...(delta.endpoints?.removedIds ?? []),
            ],
          },
          dbTables: {
            added: [
              ...(existing.dbTables?.added ?? []),
              ...(delta.dbTables?.added ?? []),
            ],
            removedIds: [
              ...(existing.dbTables?.removedIds ?? []),
              ...(delta.dbTables?.removedIds ?? []),
            ],
          },
          uxFlows: {
            added: [
              ...(existing.uxFlows?.added ?? []),
              ...(delta.uxFlows?.added ?? []),
            ],
            removedIds: [
              ...(existing.uxFlows?.removedIds ?? []),
              ...(delta.uxFlows?.removedIds ?? []),
            ],
          },
        };
        return {
          ...prev,
          customizations: {
            ...prev.customizations,
            [featureSlug]: merged,
          } as TechSpecCustomizations,
        };
      });
    },
    [],
  );

  function handleCopyLink() {
    navigator.clipboard.writeText(window.location.href);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  }

  const featureList = specFeatures.map((sf) => ({
    slug: sf.feature.slug,
    name: sf.feature.name,
  }));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-24 lg:pb-8">
      {/* Header */}
      <div className="mb-6">
        <Link
          to={`/specs/${spec.slug}/build${searchParams.get('config') ? `?config=${searchParams.get('config')}` : ''}`}
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-3"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to builder
        </Link>
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <Code2 className="h-5 w-5 text-indigo-500" />
              <h1 className="text-2xl font-bold text-gray-900">
                Technical Spec: {spec.name}
              </h1>
            </div>
            <p className="text-sm text-gray-500">
              Choose your stack — pages, endpoints, and DB tables update
              automatically
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleCopyLink}
              className="flex items-center gap-1.5 whitespace-nowrap px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
            >
              <LinkIcon className="h-4 w-4 flex-shrink-0" />
              {copiedLink ? 'Copied!' : 'Copy link'}
            </button>
            <button
              type="button"
              onClick={() => setShowExport(true)}
              className="flex items-center gap-2 whitespace-nowrap px-4 py-1.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors"
            >
              <Share2 className="h-4 w-4 flex-shrink-0" />
              Export
            </button>
          </div>
        </div>
      </div>

      {/* Mobile tab bar (top) */}
      <div className="lg:hidden flex rounded-xl border border-gray-200 overflow-hidden mb-4">
        {(['stack', 'spec'] as MobileTab[]).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setMobileTab(t)}
            className={`flex-1 py-2.5 text-sm font-medium capitalize transition-colors ${
              mobileTab === t
                ? 'bg-indigo-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            {t === 'stack' ? 'Tech Stack' : 'Spec'}
          </button>
        ))}
      </div>

      {/* Desktop layout */}
      <div className="hidden lg:grid grid-cols-4 gap-6">
        {/* Left: Stack selector */}
        <div className="col-span-1">
          <div className="bg-white rounded-xl border border-gray-200 p-4 sticky top-20">
            <h2 className="text-sm font-semibold text-gray-700 mb-4">
              Tech Stack
            </h2>
            <StackSelector
              value={techState.stack}
              onChange={handleStackChange}
            />
          </div>
        </div>

        {/* Right: Feature tech cards */}
        <div className="col-span-3 space-y-4">
          {enabledFeatures.length === 0 && (
            <div className="text-center py-16 text-gray-400">
              <Code2 className="h-8 w-8 mx-auto mb-3 opacity-40" />
              <p className="text-sm">No features enabled.</p>
              <p className="text-xs mt-1">
                <Link
                  to={`/specs/${spec.slug}/build`}
                  className="text-indigo-500 hover:underline"
                >
                  Go back to the builder
                </Link>{' '}
                and enable some features.
              </p>
            </div>
          )}
          {enabledFeatures.map((sf) => (
            <FeatureTechCard
              key={sf.feature.slug}
              featureSlug={sf.feature.slug}
              featureName={sf.feature.name}
              stack={techState.stack}
              featureConfig={builderConfig?.features[sf.feature.slug]}
              customizations={techState.customizations[sf.feature.slug]}
              onCustomize={(delta) => handleCustomize(sf.feature.slug, delta)}
            />
          ))}
        </div>
      </div>

      {/* Mobile layout */}
      <div className="lg:hidden">
        {mobileTab === 'stack' && (
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <StackSelector
              value={techState.stack}
              onChange={handleStackChange}
            />
          </div>
        )}
        {mobileTab === 'spec' && (
          <div className="space-y-4">
            {enabledFeatures.map((sf) => (
              <FeatureTechCard
                key={sf.feature.slug}
                featureSlug={sf.feature.slug}
                featureName={sf.feature.name}
                stack={techState.stack}
                featureConfig={builderConfig?.features[sf.feature.slug]}
                customizations={techState.customizations[sf.feature.slug]}
                onCustomize={(delta) => handleCustomize(sf.feature.slug, delta)}
              />
            ))}
          </div>
        )}
      </div>

      {showExport && (
        <TechExportModal
          spec={spec}
          builderConfig={builderConfig}
          techState={techState}
          features={featureList}
          onClose={() => setShowExport(false)}
        />
      )}
    </div>
  );
}
